import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Newspaper question data from the backend.
 */
interface NewspaperQuestion {
    id: number;
    question: string;
    answer: string | null;
    is_answered: boolean;
    is_published: boolean;
    user: {
        id: number;
        name: string;
    } | null;
    answerer: {
        id: number;
        name: string;
    } | null;
    answered_at: string | null;
    created_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated questions response.
 */
interface PaginatedQuestions {
    data: NewspaperQuestion[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Questions Index page.
 */
interface Props {
    questions: PaginatedQuestions;
    filters: {
        status: string | null;
        search: string | null;
    };
}

/**
 * GM Newspaper Questions Index page - displays all questions with management actions.
 */
export default function Index({ questions, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [statusFilter, setStatusFilter] = useState(filters.status || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/newspaper/questions', {
            search: search || undefined,
            status: statusFilter || undefined,
        }, { preserveState: true });
    }, [search, statusFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('');
        router.get('/gm/newspaper/questions', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (question: NewspaperQuestion) => {
        if (confirm('Are you sure you want to delete this question?')) {
            router.delete(`/gm/newspaper/questions/${question.id}`, { preserveScroll: true });
        }
    };

    // Handle toggle publish
    const handleTogglePublish = (question: NewspaperQuestion) => {
        router.patch(`/gm/newspaper/questions/${question.id}/toggle-publish`, {}, { preserveScroll: true });
    };

    return (
        <GmLayout title="Ask the Feywild Times" activePath="/gm/newspaper">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">❓ Ask the Feywild Times</h1>
                    <Link
                        href="/gm/newspaper"
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    >
                        ← Back to Articles
                    </Link>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Question or answer text"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="unanswered">Unanswered</option>
                                <option value="answered">Answered</option>
                                <option value="published">Published</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Questions List */}
                {questions.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No questions found. Questions submitted by players will appear here.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {questions.data.map((question) => (
                            <QuestionCard
                                key={question.id}
                                question={question}
                                onDelete={handleDelete}
                                onTogglePublish={handleTogglePublish}
                            />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {questions.last_page > 1 && (
                    <Pagination links={questions.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * QuestionCard - Displays a single question with actions.
 */
function QuestionCard({
    question,
    onDelete,
    onTogglePublish,
}: {
    question: NewspaperQuestion;
    onDelete: (question: NewspaperQuestion) => void;
    onTogglePublish: (question: NewspaperQuestion) => void;
}) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
                <div>
                    <span className="text-sm text-gray-400">
                        Asked by {question.user?.name || 'Unknown'} on{' '}
                        {new Date(question.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2 mt-1">
                        {question.is_answered ? (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-600 text-white">Answered</span>
                        ) : (
                            <span className="px-2 py-0.5 text-xs rounded bg-yellow-600 text-white">Pending</span>
                        )}
                        {question.is_published && (
                            <span className="px-2 py-0.5 text-xs rounded bg-green-600 text-white">Published</span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <Link
                        href={`/gm/newspaper/questions/${question.id}/edit`}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        {question.is_answered ? 'Edit Answer' : 'Answer'}
                    </Link>
                    {question.is_answered && (
                        <button
                            onClick={() => onTogglePublish(question)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                                question.is_published
                                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                    : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                        >
                            {question.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                    )}
                    <button
                        onClick={() => onDelete(question)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </div>

            <p className="text-white font-medium mb-2">{question.question}</p>

            {question.answer && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                    <p className="text-sm text-gray-400 mb-1">
                        Answered by {question.answerer?.name || 'Unknown'}
                    </p>
                    <p className="text-gray-300 text-sm line-clamp-3">{question.answer}</p>
                </div>
            )}
        </div>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

